import "./WorkflowTaskPanel.css";

import { ReactElement, useMemo } from "react";

import { SelectData } from "../../models";
import { GetTaskBoardResponse, SearchWorkflowTasksResponse } from "../../service/query";
import { EmptyFlyoutPanel } from "../../widget";
import { CompleteTaskModal, TaskPanelLeft, TaskPanelRight, TaskPanelTop } from "./components";
import { DeleteTaskModal } from "./components/DeleteTaskModal";
import { TaskPanelContextValue, TaskPanelProvider } from "./TaskPanelContext";
import { useWorkflowTaskPanel } from "./useWorkflowTaskPanel";

interface WorkflowTaskPanelProps {
  /*
    If undefined, then create
    If defined, then update
  */
  task?: SearchWorkflowTasksResponse["results"][0];
  currentStepUuid: string | undefined;
  workflow?: GetTaskBoardResponse["results"][number];
  // Create-mode task type (REQ-010). Ignored on edit, where the type is derived from the task.
  isGeneralTask?: boolean;
  // Parent (project / deal / landholding) name shown under the title. On edit it comes from the
  // task itself; on create the caller supplies it, as there is no task to read it from.
  parentName?: string;
  /*
    Parent object a created general task links to, for hosts with no workflow to derive it from — a project
    with no workflow applied still has general tasks. Ignored when a workflow supplies the parent.
  */
  parentObjectType?: string;
  parentObjectUuid?: string;
  show: boolean;
  phaseOptions?: SelectData;
  phaseLabel?: string;
  isRepeatablePhase?: (phase: string | null | undefined) => boolean;

  refresh: boolean;
  handleRefresh: () => void;

  onClose: () => void;
}

export const WorkflowTaskPanel = ({
  task,
  currentStepUuid,
  workflow,
  isGeneralTask: isGeneralTaskProp,
  parentName,
  parentObjectType: parentObjectTypeProp,
  parentObjectUuid: parentObjectUuidProp,
  show,
  phaseOptions,
  phaseLabel,
  isRepeatablePhase,

  refresh,
  handleRefresh,

  onClose,
}: WorkflowTaskPanelProps): JSX.Element => {
  // A caller can still be holding the previously selected task's workflow while the new one
  // loads. Never derive steps or organisation access from a workflow that isn't this task's —
  // falling back to undefined shows the empty state, which fails closed on permissions.
  const taskWorkflow = task && workflow?.uuid !== task.workflowUuid ? undefined : workflow;

  const panel = useWorkflowTaskPanel({
    task,
    currentStepUuid,
    workflow: taskWorkflow,
    isGeneralTask: isGeneralTaskProp,
    parentObjectType: parentObjectTypeProp,
    parentObjectUuid: parentObjectUuidProp,
    show,
    refresh,
    handleRefresh,

    onClose,
  });

  // The parts read this instead of taking ~20 props each; it changes whenever the panel's state does.
  const contextValue = useMemo<TaskPanelContextValue>(
    () => ({
      ...panel,
      task,
      workflow: taskWorkflow,
      parentName: task?.workflowObjectName ?? parentName,
      phaseOptions,
      phaseLabel,
      isRepeatablePhase,
      handleRefresh,
    }),
    [panel, task, taskWorkflow, parentName, phaseOptions, phaseLabel, isRepeatablePhase, handleRefresh]
  );

  const content = (): ReactElement => {
    return (
      <div className="WorkflowTaskPanelContent">
        <TaskPanelTop />
        <TaskPanelLeft />
        {/* General tasks have a synthesised access level, so the right rail renders for them too. */}
        {(panel.workflowOrganisationAccessLevel || panel.isGeneralTask) && <TaskPanelRight />}
        <DeleteTaskModal
          show={panel.showDeleteTaskModal}
          closeModal={() => panel.setShowDeleteTaskModal(false)}
          closePanel={panel.handleCloseTaskPanel}
          handleRefresh={handleRefresh}
          task={task}
        />
        <CompleteTaskModal
          show={panel.showCompletePhaseModal}
          task={panel.currentTask}
          closeModal={() => panel.setShowCompletePhaseModal(false)}
          closePanel={panel.handleCloseTaskPanel}
          handleRefresh={handleRefresh}
          setErrors={panel.setErrors}
        />
      </div>
    );
  };

  return (
    <TaskPanelProvider value={contextValue}>
      <EmptyFlyoutPanel show={show} onClose={panel.handleCloseTaskPanel} size="Large" contentChildren={content()} />
    </TaskPanelProvider>
  );
};
